import { useState, type ReactNode } from "react"

import { Button, DropdownMenuItem, Input } from "@coval/ui"
import { format, isValid, parseISO } from "date-fns"
import { Check, RefreshCw } from "lucide-react"

import {
  isCollectionFilterActive,
  type CollectionFilterControls,
  type CollectionFilterValue,
} from "@/hooks/useCollectionFilters"

import { ActiveFilterBar, AddFilterButton } from "./ActiveFilterBar"
import { CollectionDateFilter } from "./CollectionDateFilter"
import { EditableFilterChip } from "./EditableFilterChip"
import { FilterPicker, type FilterPickerItem } from "./FilterPicker"
import { FilterPropertyIcon } from "./FilterPropertyIcon"
import { SearchableCheckboxList } from "./SearchableCheckboxList"

export function CollectionFilter({
  controls,
  items = [],
  activeCount = 0,
  onReset,
  children,
  open,
  onOpenChange,
  label = "Filter",
  section: controlledSection,
  onSectionChange,
  bleed,
}: {
  section?: string | null
  onSectionChange?: (section: string | null) => void
  controls: CollectionFilterControls
  items?: FilterPickerItem[]
  activeCount?: number
  onReset?: () => void
  children?: ReactNode
  open?: boolean
  onOpenChange?: (open: boolean) => void
  label?: string
  /** See `ActiveFilterBar`; for hosts that do not pad the band's gutter. */
  bleed?: boolean
}) {
  const [localOpen, setLocalOpen] = useState(false)
  const [localSection, setLocalSection] = useState<string | null>(null)
  const section =
    controlledSection === undefined ? localSection : controlledSection
  const setSection = (next: string | null) => {
    setLocalSection(next)
    onSectionChange?.(next)
  }
  const changeOpen = (next: boolean) => {
    setLocalOpen(next)
    onOpenChange?.(next)
  }
  const pickerItems = [
    ...items,
    ...controls.properties.map((property) => {
      const value = controls.values[property.id] ?? {}
      return {
        id: property.id,
        label: property.label,
        icon: <FilterPropertyIcon id={property.id} kind={property.kind} />,
        activeCount: isCollectionFilterActive(value)
          ? (value.selected?.length ?? 1)
          : 0,
        content:
          property.kind === "select" && property.unavailable ? (
            <div className="p-1">
              <p
                role="status"
                className="px-2 py-1.5 text-sm text-coval-text-muted"
              >
                {property.unavailable.message}
              </p>
              {property.unavailable.state === "error" && (
                <DropdownMenuItem onSelect={property.unavailable.onRetry}>
                  <RefreshCw className="size-4" />
                  Retry
                </DropdownMenuItem>
              )}
            </div>
          ) : property.kind === "select" && property.single ? (
            <div className="p-1">
              {property.options.map((option) => {
                const checked = value.selected?.[0] === option.id
                return (
                  <DropdownMenuItem
                    key={option.id}
                    role="menuitemcheckbox"
                    aria-checked={checked}
                    onSelect={() =>
                      controls.onChange(property.id, {
                        selected: checked ? [] : [option.id],
                      })
                    }
                  >
                    <span className="flex-1">{option.label}</span>
                    {checked && <Check className="size-4" />}
                  </DropdownMenuItem>
                )
              })}
            </div>
          ) : property.kind === "select" ? (
            <SearchableCheckboxList
              items={property.options}
              selectedIds={value.selected ?? []}
              onSelectionChange={(selected) =>
                controls.onChange(property.id, { selected })
              }
              isOpen
              isLoading={controls.isLoading ?? false}
              placeholder={`Search ${property.label.toLowerCase()}…`}
              emptyMessage="No matching values"
              noItemsMessage="No values available"
              capitalizeLabels={false}
            />
          ) : property.kind === "date" ? (
            <CollectionDateFilter
              value={value}
              onChange={(next) => controls.onChange(property.id, next)}
            />
          ) : (
            <div className="space-y-3 p-3">
              {property.kind === "text" ? (
                <Input
                  aria-label={`${property.label} contains`}
                  placeholder="Contains…"
                  value={value.text ?? ""}
                  onChange={(event) =>
                    controls.onChange(property.id, {
                      text: event.target.value,
                    })
                  }
                />
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {(["min", "max"] as const).map((bound) => (
                    <label key={bound} className="space-y-1 text-sm">
                      {bound === "min" ? "Minimum" : "Maximum"}
                      <Input
                        type="number"
                        step="any"
                        aria-label={`${property.label} ${bound === "min" ? "from" : "to"}`}
                        value={value[bound] ?? ""}
                        onChange={(event) =>
                          controls.onChange(property.id, {
                            ...value,
                            [bound]: event.target.value,
                          })
                        }
                      />
                    </label>
                  ))}
                </div>
              )}
              <Button
                variant="ghost"
                disabled={!isCollectionFilterActive(value)}
                onClick={() => controls.onChange(property.id, {})}
              >
                Clear {property.label.toLowerCase()}
              </Button>
            </div>
          ),
      }
    }),
  ]

  return (
    <div className="flex flex-wrap items-center gap-2">
      <FilterPicker
        label={label}
        open={open ?? localOpen}
        onOpenChange={changeOpen}
        section={section}
        onSectionChange={setSection}
        activeCount={activeCount + controls.activeCount}
        items={pickerItems}
      />
      {activeCount + controls.activeCount > 0 && (
        <ActiveFilterBar
          bleed={bleed}
          onClear={() => {
            controls.reset()
            onReset?.()
            changeOpen(false)
          }}
          addControl={
            <FilterPicker
              label={label}
              activeCount={activeCount + controls.activeCount}
              items={pickerItems}
              trigger={<AddFilterButton />}
            />
          }
        >
          {children}
          {controls.properties
            .filter((property) =>
              isCollectionFilterActive(controls.values[property.id] ?? {}),
            )
            .map((property) => {
              const value: CollectionFilterValue = controls.values[property.id]
              const formatBound = (bound: string | undefined) => {
                if (!bound) {
                  return "Any"
                }
                const date = property.kind === "date" ? parseISO(bound) : null
                return date && isValid(date)
                  ? format(date, "MMM d, yyyy HH:mm")
                  : bound
              }
              const summary =
                value.selected
                  ?.map(
                    (id) =>
                      property.options.find((option) => option.id === id)
                        ?.label ?? id,
                  )
                  .join(", ") ||
                value.text ||
                `${formatBound(value.min)} – ${formatBound(value.max)}`
              return (
                <EditableFilterChip
                  key={property.id}
                  property={property.label}
                  value={summary}
                  onEdit={() => {
                    setSection(property.id)
                    changeOpen(true)
                  }}
                  onRemove={() => controls.onChange(property.id, {})}
                />
              )
            })}
        </ActiveFilterBar>
      )}
    </div>
  )
}
